import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SlideOver } from '../../shared/SlideOver';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { Input } from '../../../components/ui/Input';
import { StudentData } from '../pages/StudentDetailPanel';
import { GroupId } from '../../../config/systemConfig';
import { DOMAIN_META, getSubTestDefinition, DOMAIN_MODULE_MAP, isBegrijpendLezenFamily, isTaalverzorgingTaaFamily, type AssessmentDomainId } from '../../../assessment';
import { error as logError } from '../../../utils/logger';
import { genId } from '../../../utils/genId';
import { dataLayer } from '../../../data/dataLayer';
import { Clock, HelpCircle, Users, Calendar, CheckCircle2, Search, ChevronDown, ChevronRight, Check, CheckSquare, UserCheck, Layers } from 'lucide-react';
import { FAMILIES_BY_MODULE, type FamilyDefinition } from '../../../content';
import { DjayNarrator } from '../../../mascot';
import { X } from 'lucide-react';
interface CreateAssessmentPanelProps {
  open: boolean;
  onClose: () => void;
  students: StudentData[];
  selectedGroup: GroupId;
  teacherId: string;
  classroomId: string | null;
  onSuccess: () => void;
}
type Step = 1 | 2 | 3;
function getFamiliesForDomain(domainId: AssessmentDomainId): FamilyDefinition[] {
  const modules = DOMAIN_MODULE_MAP[domainId];
  let families = modules.flatMap(mod => FAMILIES_BY_MODULE.get(mod) || []);
  if (domainId === 'taalverzorging') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isTaalverzorgingTaaFamily(f.familyId));
  } else if (domainId === 'begrijpend-lezen') {
    families = families.filter(f => f.module !== 'Taalbegrip' || isBegrijpendLezenFamily(f.familyId));
  }
  return families;
}
const STEP_CONFIG = [{
  num: 1,
  label: 'Vakgebied'
}, {
  num: 2,
  label: 'Onderdelen'
}, {
  num: 3,
  label: 'Klaarzetten'
}] as const;
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 60 : -60,
    opacity: 0
  }),
  center: {
    x: 0,
    opacity: 1
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -60 : 60,
    opacity: 0
  })
};
export function CreateAssessmentPanel({
  open,
  onClose,
  students,
  selectedGroup,
  teacherId,
  classroomId,
  onSuccess
}: CreateAssessmentPanelProps) {
  const [step, setStep] = useState<Step>(1);
  const [direction, setDirection] = useState(1);
  const [selectedDomain, setSelectedDomain] = useState<AssessmentDomainId | null>(null);
  const [selectedFamilyIds, setSelectedFamilyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSkills, setExpandedSkills] = useState<Record<string, boolean>>({});
  const [selectionMode, setSelectionMode] = useState<'whole' | 'specific' | null>(null);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [deadline, setDeadline] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [hideTip, setHideTip] = useState(false);
  useEffect(() => {
    const pref = localStorage.getItem('mp.teacher.hideAssessmentTip');
    if (pref === 'true') setHideTip(true);
  }, []);
  const dismissTip = () => {
    setHideTip(true);
    localStorage.setItem('mp.teacher.hideAssessmentTip', 'true');
  };
  useEffect(() => {
    if (open) {
      setStep(1);
      setDirection(1);
      setSelectedDomain(null);
      setSelectionMode(null);
      setSelectedStudentIds([]);
      setSelectedFamilyIds([]);
      setDeadline('');
      setSearchQuery('');
      setExpandedSkills({});
      setIsSubmitting(false);
      setShowSuccess(false);
    }
  }, [open]);
  useEffect(() => {
    if (selectedDomain) {
      setSelectedFamilyIds([]);
      setSearchQuery('');
      setExpandedSkills({});
    }
  }, [selectedDomain]);
  const handleNext = () => {
    if (step < 3) {
      setDirection(1);
      setStep(s => s + 1 as Step);
    }
  };
  const handleBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep(s => s - 1 as Step);
    }
  };
  const handleConfirm = async () => {
    if (!selectedDomain) return;
    setIsSubmitting(true);
    const domainDef = getSubTestDefinition(selectedDomain, selectedGroup);
    const questionCount = domainDef?.questionCount || 0;
    const estimatedMinutes = domainDef?.estimatedMinutes || 0;
    const assignmentId = genId('assignment');
    const allFamilies = getFamiliesForDomain(selectedDomain);
    const selectedFams = allFamilies.filter(f => selectedFamilyIds.includes(f.familyId));
    const uniqueSkills = Array.from(new Set(selectedFams.map(f => f.skill.replace(/_/g, ' '))));
    const skillSummary = uniqueSkills.join(', ');
    const wholeClass = selectionMode === 'whole';
    const assignment = {
      id: assignmentId,
      teacherId,
      classroomId,
      domainId: selectedDomain,
      domainLabel: DOMAIN_META[selectedDomain].label,
      group: selectedGroup,
      questionCount,
      estimatedMinutes,
      targetStudentIds: wholeClass ? [] : selectedStudentIds,
      wholeClass,
      selectedFamilyIds: selectedFamilyIds,
      type: 'targeted' as const,
      selectedSkillSummary: skillSummary,
      createdAt: Date.now(),
      deadline: deadline ? new Date(deadline).getTime() : null,
      status: 'active' as const
    };
    try {
      await dataLayer.saveTeacherAssignment(assignmentId, assignment);
      setShowSuccess(true);
    } catch (error) {
      logError('Failed to save assignment', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  const domains: AssessmentDomainId[] = ['rekenen', 'taalverzorging', 'begrijpend-lezen'];
  const toggleStudent = (id: string) => {
    setSelectedStudentIds(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  };
  const isNextDisabled = () => {
    if (step === 1 && !selectedDomain) return true;
    if (step === 2 && selectedFamilyIds.length === 0) return true;
    if (step === 3 && selectionMode === null) return true;
    if (step === 3 && selectionMode === 'specific' && selectedStudentIds.length === 0) return true;
    return false;
  };
  // ── Step indicator ──────────────────────────────────────────────────────
  const renderStepIndicator = () => <div className="flex items-center gap-1 mb-4" data-id="element-9072" data-mp="1hk9iwujfnhjo">
      {STEP_CONFIG.map((s, i) => {
      const isActive = step === s.num;
      const isDone = step > s.num;
      return <Fragment key={s.num} data-id="element-9073" data-mp="17laiu0cqgu8a" data-mpi={i}>
            {i !== 0 && <div className={`flex-1 h-[2px] rounded-full transition-colors duration-300 ${isDone ? 'bg-orange-500' : 'bg-slate-200'}`} data-id="element-9074" data-mp="17nwofug2cznv" />}
            <div className="flex flex-col items-center gap-1" data-id="element-9075" data-mp="3sdm2i58y0hfi">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all duration-300 ${isDone ? 'bg-orange-500 text-white' : isActive ? 'bg-orange-500 text-white ring-4 ring-orange-100' : 'bg-slate-100 text-slate-400'}`} data-id="element-9076" data-mp="3krqnwj10qfbe">
                {isDone ? <Check className="w-3.5 h-3.5" data-id="element-9077" data-mp="3pso3cnun8v97" /> : s.num}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${isActive ? 'text-orange-600' : isDone ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-9078" data-mp="321ioaawj26zy">
                {s.label}
              </span>
            </div>
          </Fragment>;
    })}
    </div>;
  // ── Running summary strip ───────────────────────────────────────────────
  const renderSummaryStrip = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    const hasDomain = !!selectedDomain;
    const hasContent = selectedFamilyIds.length > 0;
    const hasStudents = selectionMode !== null;
    if (!hasDomain && step === 1) return null;
    return <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} className="border-t border-slate-100 pt-3 pb-1" data-id="element-9079" data-mp="1xt0u34g0rlun">
        <div className="flex items-center gap-3 flex-wrap" data-id="element-9080" data-mp="26qkwigpmbo6u">
          {hasDomain && meta && Icon && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9081" data-mp="2bq64fgmrn6vp">
              <div className={`w-4 h-4 rounded flex items-center justify-center ${meta.bgColor} ${meta.color}`} data-id="element-9082" data-mp="2li28d8bj9r7u">
                <Icon className="w-2.5 h-2.5" data-id="element-9083" data-mp="2qgdgnem6jtny" />
              </div>
              <span className="text-xs font-semibold text-slate-700" data-id="element-9084" data-mp="2uj9rgj5halhc">
                {meta.shortLabel}
              </span>
            </div>}
          {hasContent && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9085" data-mp="ntzrv0zf4f78">
              <Layers className="w-3 h-3 text-slate-500" data-id="element-9086" data-mp="iuvh45wui9es" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9087" data-mp="ixhpg7rk8sy2">
                {selectedFamilyIds.length} onderdelen
              </span>
            </div>}
          {hasStudents && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9088" data-mp="9wtanauceok4">
              <UserCheck className="w-3 h-3 text-slate-500" data-id="element-9089" data-mp="12je36emk16p9" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9090" data-mp="12jxgfi19f6wy">
                {selectionMode === 'whole' ? `${students.length} leerlingen` : `${selectedStudentIds.length} leerling(en)`}
              </span>
            </div>}
          {deadline && <div className="flex items-center gap-1.5 bg-slate-50 rounded-full px-2.5 py-1 border border-slate-200" data-id="element-9091" data-mp="1qat88f5pu247">
              <Calendar className="w-3 h-3 text-slate-500" data-id="element-9092" data-mp="1jly8b4s4dpb8" />
              <span className="text-xs font-semibold text-slate-700" data-id="element-9093" data-mp="1jmhnidqdxzlk">
                {new Date(deadline).toLocaleDateString('nl-NL', {
              day: 'numeric',
              month: 'short'
            })}
              </span>
            </div>}
        </div>
      </motion.div>;
  };
  // ── Step 1: Domain ──────────────────────────────────────────────────────
  const renderStep1 = () => <div className="flex flex-col h-full" data-id="element-9094" data-mp="22ir3j6kul087">
      <div className="flex items-center gap-2.5 mb-4" data-id="element-9095" data-mp="1xmir40y1bl4q">
        <p className="text-sm font-medium text-slate-600" data-id="element-9096" data-mp="1xjwjy2f237n1">
          Kies het vakgebied voor{' '}
          <span className="font-bold text-slate-900" data-id="element-9097" data-mp="2ccg7vvtmx8jw">
            groep {selectedGroup}
          </span>
        </p>
      </div>

      {!hideTip && <div className="flex items-start gap-3 p-3 mb-4 bg-orange-50/60 border border-orange-100 rounded-xl" data-id="element-9098" data-mp="2lcd36gaj55nx">
          <DjayNarrator size="xs" emotion="pointing" data-id="element-9099" data-mp="2gdjlscs4guih" />
          <div className="flex-1 text-xs text-slate-700 mt-0.5 leading-relaxed" data-id="element-9100" data-mp="2zya36w82d0yr">
            <strong data-id="element-9101" data-mp="2zw7fwjyaplxq">Een vakgebied</strong> is een groot onderdeel zoals Rekenen
            of Taalverzorging. Kies waarover deze toets moet gaan.
          </div>
          <button onClick={dismissTip} aria-label="Sluit tip" className="text-slate-400 hover:text-slate-600 p-1 -mr-1 -mt-1" data-id="element-9102" data-mp="2qwkdpvxqsc3m">
            <X className="w-4 h-4" data-id="element-9103" data-mp="2h7966dqi7fhu" />
          </button>
        </div>}

      <div className="space-y-2.5 flex-1" data-id="element-9104" data-mp="2ly95tqr0cbo1">
        {domains.map(domainId => {
        const meta = DOMAIN_META[domainId];
        const def = getSubTestDefinition(domainId, selectedGroup);
        const isAvailable = def !== null && def.available;
        const isSelected = selectedDomain === domainId;
        return <div key={domainId} onClick={() => isAvailable && setSelectedDomain(domainId)} className={`p-3.5 rounded-xl border-2 transition-all cursor-pointer ${!isAvailable ? 'opacity-40 border-slate-100 bg-slate-50 cursor-not-allowed' : isSelected ? 'border-orange-500 bg-orange-50/60 shadow-sm' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-9105" data-mp="1y9pu118r1gr7">
              <div className="flex items-center gap-3.5" data-id="element-9106" data-mp="3v7x8ir29zino">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${meta.bgColor} ${meta.color}`} data-id="element-9107" data-mp="3r5sh7yzauy47">
                  <meta.icon className="w-5 h-5" data-id="element-9108" data-mp="roawe0kozamg" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-9109" data-mp="mrtzagvillx8">
                  <div className="flex items-center justify-between" data-id="element-9110" data-mp="ipgupvsi7u21">
                    <h3 className={`font-bold text-[15px] ${isSelected ? 'text-orange-900' : 'text-slate-900'}`} data-id="element-9111" data-mp="irtmf0o2disa">
                      {meta.label}
                    </h3>
                    {isSelected && <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" data-id="element-9112" data-mp="2yftmbmyfc6rm" />}
                  </div>
                  <div className="flex items-center gap-3 mt-1" data-id="element-9113" data-mp="1e0ngbh8dgmsq">
                    <span className="text-xs text-slate-500" data-id="element-9114" data-mp="1iyyprqur8vaf">
                      {meta.description}
                    </span>
                  </div>
                  {isAvailable && def && <div className="flex items-center gap-3 mt-1.5 text-[11px] font-bold text-slate-400" data-id="element-9115" data-mp="1rqh7w7b9hl65">
                      <span className="flex items-center gap-1" data-id="element-9116" data-mp="1wo8xuvscix7p">
                        <HelpCircle className="w-3 h-3" data-id="element-9117" data-mp="21o51gtx4smmq" />
                        {def.questionCount} vr
                      </span>
                      <span className="flex items-center gap-1" data-id="element-9118" data-mp="26oj6s47clih2">
                        <Clock className="w-3 h-3" data-id="element-9119" data-mp="2aotdjj2yscwz" />~{def.estimatedMinutes} min
                      </span>
                    </div>}
                  {!isAvailable && <span className="mt-1.5 text-[11px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded inline-block" data-id="element-9120" data-mp="gb2aj2f54rai">
                      Niet beschikbaar
                    </span>}
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
  // ── Step 2: Family picker ───────────────────────────────────────────────
  const renderStep2 = () => {
    const allFamilies = selectedDomain ? getFamiliesForDomain(selectedDomain) : [];
    const filteredFamilies = allFamilies.filter(f => f.description.toLowerCase().includes(searchQuery.toLowerCase()) || f.skill.toLowerCase().includes(searchQuery.toLowerCase()));
    const grouped: Record<string, typeof allFamilies> = {};
    filteredFamilies.forEach(f => {
      if (!grouped[f.skill]) grouped[f.skill] = [];
      grouped[f.skill].push(f);
    });
    const toggleFamily = (id: string) => {
      setSelectedFamilyIds(prev => prev.includes(id) ? prev.filter(fid => fid !== id) : [...prev, id]);
    };
    const toggleSkillGroup = (familiesInSkill: FamilyDefinition[]) => {
      const ids = familiesInSkill.map(f => f.familyId);
      const allSelected = ids.every(id => selectedFamilyIds.includes(id));
      if (allSelected) {
        setSelectedFamilyIds(prev => prev.filter(id => !ids.includes(id)));
      } else {
        setSelectedFamilyIds(prev => Array.from(new Set([...prev, ...ids])));
      }
    };
    const toggleExpand = (skill: string) => {
      setExpandedSkills(prev => ({
        ...prev,
        [skill]: !prev[skill]
      }));
    };
    const handleSelectAll = () => {
      if (selectedFamilyIds.length === allFamilies.length) {
        setSelectedFamilyIds([]);
      } else {
        setSelectedFamilyIds(allFamilies.map(f => f.familyId));
      }
    };
    const selectedCount = selectedFamilyIds.length;
    const isAllSelected = allFamilies.length > 0 && selectedCount === allFamilies.length;
    return <div className="flex flex-col h-full" data-id="element-9121" data-mp="3pobnuwneypzp">
        <div className="flex items-center gap-2.5 mb-3" data-id="element-9122" data-mp="3pl7c9e7mcrz0">
          <p className="text-sm font-medium text-slate-600" data-id="element-9123" data-mp="1v60j1bgku7qn">
            Welke onderdelen wil je toetsen?
          </p>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between mb-2" data-id="element-9124" data-mp="1lh8tf9yznziw">
          <div className="flex items-center gap-2" data-id="element-9125" data-mp="1lffnjww79jby">
            <span className="text-sm font-bold text-orange-600" data-id="element-9126" data-mp="1hg6uku8zhphg">
              {selectedCount}
            </span>
            <span className="text-xs text-slate-500" data-id="element-9127" data-mp="1chls0o69etpu">
              / {allFamilies.length} geselecteerd
            </span>
          </div>
          <button onClick={handleSelectAll} className="flex items-center gap-1 text-xs font-semibold text-orange-600 hover:text-orange-700 px-2 py-1 rounded-lg hover:bg-orange-50 transition-colors" data-id="element-9128" data-mp="17jka46kkl0fy">
            <CheckSquare className="w-3.5 h-3.5" data-id="element-9129" data-mp="2ww5l6rmk52ty" />
            {isAllSelected ? 'Deselecteer' : 'Alles'}
          </button>
        </div>

        {/* Search */}
        <div className="relative mb-3" data-id="element-9130" data-mp="2svlmvkq8zt68">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-9131" data-mp="enb4rsr8cg96" />
          <Input placeholder="Zoek..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-white h-10 text-sm" data-id="element-9132" data-mp="jlw47pynqvwk" />
        </div>

        {/* Scrollable skill groups */}
        <div className="flex-1 overflow-y-auto -mx-1 px-1 space-y-1.5 pb-2" data-id="element-9133" data-mp="sdocpv44fuyi">
          {Object.entries(grouped).map(([skill, families]) => {
          const isExpanded = expandedSkills[skill];
          const familyIds = families.map(f => f.familyId);
          const selectedInSkill = familyIds.filter(id => selectedFamilyIds.includes(id)).length;
          const isAllSkillSelected = selectedInSkill === families.length;
          const isPartiallySelected = selectedInSkill > 0 && selectedInSkill < families.length;
          return <div key={skill} className="border border-slate-200 rounded-lg overflow-hidden bg-white" data-id="element-9134" data-mp="1l1adywpvmni7">
                <div className="flex items-center justify-between min-h-[44px] px-3" data-id="element-9135" data-mp="rs02613y8b0y">
                  <div className="flex items-center gap-2 cursor-pointer flex-1 py-2" onClick={() => toggleExpand(skill)} data-id="element-9136" data-mp="15imu9af7f2o2">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" data-id="element-9137" data-mp="1fg9iwomb28l8" /> : <ChevronRight className="w-4 h-4 text-slate-400" data-id="element-9138" data-mp="1air96w0grpc6" />}
                    <span className="text-sm font-semibold text-slate-700 truncate" data-id="element-9139" data-mp="1oi0prwsl0lgo">
                      {skill.replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${isAllSkillSelected ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'}`} data-id="element-9140" data-mp="1keb5vkmuqs1z">
                      {selectedInSkill}/{families.length}
                    </span>
                  </div>
                  <div className="cursor-pointer p-2 -mr-1" onClick={() => toggleSkillGroup(families)} data-id="element-9141" data-mp="3g5fb2p3qfqgu">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${isAllSkillSelected ? 'bg-orange-500 border-orange-500 text-white' : isPartiallySelected ? 'bg-orange-100 border-orange-400 text-orange-500' : 'border-slate-300 bg-white'}`} data-id="element-9142" data-mp="37cvhw467me3e">
                      {isAllSkillSelected && <Check className="w-3 h-3" data-id="element-9143" data-mp="2jg7u7ue265j9" />}
                      {isPartiallySelected && <div className="w-2 h-0.5 bg-current rounded-full" data-id="element-9144" data-mp="2oej6scvqmu56" />}
                    </div>
                  </div>
                </div>

                {isExpanded && <div className="border-t border-slate-100" data-id="element-9145" data-mp="mm4hgsqv6neq">
                    {families.map(f => {
                const isSelected = selectedFamilyIds.includes(f.familyId);
                return <div key={f.familyId} onClick={() => toggleFamily(f.familyId)} className="flex items-center gap-3 px-3 py-2.5 min-h-[44px] cursor-pointer transition-colors hover:bg-slate-50 border-b border-slate-50 last:border-b-0" data-id="element-9146" data-mp="1uzluf6lxe66u">
                          <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9147" data-mp="2dwn6tcht78kt">
                            {isSelected && <Check className="w-3 h-3" data-id="element-9148" data-mp="oh77spxln38n" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-9149" data-mp="sjk1uh2zhcpl">
                            <span className={`text-sm truncate block ${isSelected ? 'font-semibold text-slate-900' : 'text-slate-600'}`} data-id="element-9150" data-mp="sgxt4e4cwr5s">
                              {f.description}
                            </span>
                          </div>
                          <span className="text-[10px] text-slate-400 font-bold uppercase flex-shrink-0" data-id="element-9151" data-mp="uvctoi2126jh">
                            {f.levelCode}
                          </span>
                        </div>;
              })}
                  </div>}
              </div>;
        })}
          {filteredFamilies.length === 0 && <div className="text-center py-8 text-slate-400 text-sm" data-id="element-9152" data-mp="c05z3hm4cg5d">
              <div className="mb-3 flex justify-center" data-id="element-9153" data-mp="77cqginj4no0">
                <DjayNarrator size="md" emotion="thinking" data-id="element-9154" data-mp="3fnocylubrrq6" />
              </div>
              Geen resultaten voor "{searchQuery}"
            </div>}
        </div>
      </div>;
  };
  // ── Step 3: Students + Deadline + Summary ───────────────────────────────
  const renderStep3 = () => {
    const meta = selectedDomain ? DOMAIN_META[selectedDomain] : null;
    const Icon = meta?.icon;
    return <div className="flex flex-col gap-4 pb-4" data-id="element-9155" data-mp="czejcl17nj92">
        <div className="flex items-center gap-2.5" data-id="element-9156" data-mp="d0ojdfrmj1ic">
          <p className="text-sm font-medium text-slate-600" data-id="element-9157" data-mp="hzizub24cdza">
            Bijna klaar! Kies de leerlingen.
          </p>
        </div>

        {/* Student selection */}
        <div className="grid grid-cols-2 gap-2" data-id="element-9158" data-mp="3hghchkgvorg7">
          <div onClick={() => setSelectionMode('whole')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'whole' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-9159" data-mp="3lgrfco9dj1qu">
            <Users className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'whole' ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-9160" data-mp="15nirdd7988ex" />
            <div className={`text-sm font-semibold ${selectionMode === 'whole' ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-9161" data-mp="1aphwosmk1fei">
              Hele klas
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9162" data-mp="2q8ph1iinxvgo">
              {students.length} leerlingen
            </div>
          </div>
          <div onClick={() => setSelectionMode('specific')} className={`p-3 rounded-xl border-2 cursor-pointer transition-all text-center ${selectionMode === 'specific' ? 'border-orange-500 bg-orange-50/60' : 'border-slate-200 hover:border-orange-200 bg-white'}`} data-id="element-9163" data-mp="2ghcr4wgfdfda">
            <UserCheck className={`w-5 h-5 mx-auto mb-1 ${selectionMode === 'specific' ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-9164" data-mp="22gif83u64w9v" />
            <div className={`text-sm font-semibold ${selectionMode === 'specific' ? 'text-orange-900' : 'text-slate-700'}`} data-id="element-9165" data-mp="1xk1sne2mapyw">
              Selectie
            </div>
            <div className="text-[11px] text-slate-400" data-id="element-9166" data-mp="3rwrldsh91t4p">Kies leerlingen</div>
          </div>
        </div>

        {/* Student list (only when specific) */}
        {selectionMode === 'specific' && <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-9167" data-mp="8pldurdd6lgo">
            <div className="px-3 py-2 bg-slate-50 text-xs font-bold text-slate-600 border-b border-slate-200" data-id="element-9168" data-mp="3pxowe08fsw3w">
              {selectedStudentIds.length} van {students.length} geselecteerd
            </div>
            <div className="max-h-[200px] overflow-y-auto divide-y divide-slate-100" data-id="element-9169" data-mp="3h4lo06cnf9g4">
              {students.map(student => {
            const isSelected = selectedStudentIds.includes(student.id);
            return <div key={student.id} onClick={() => toggleStudent(student.id)} className={`flex items-center justify-between px-3 py-2 min-h-[44px] cursor-pointer transition-colors ${isSelected ? 'bg-orange-50/40' : 'hover:bg-slate-50'}`} data-id="element-9170" data-mp="3765ww3dmg06m">
                    <div className="flex items-center gap-2.5" data-id="element-9171" data-mp="2jk3k0xs4kea8">
                      <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600" data-id="element-9172" data-mp="2jmpup6abb9wo">
                        {student.name.charAt(0).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-slate-800" data-id="element-9173" data-mp="2nlp699qju4x3">
                        {student.name}
                      </span>
                    </div>
                    <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${isSelected ? 'bg-orange-500 border-orange-500 text-white' : 'border-slate-300 bg-white'}`} data-id="element-9174" data-mp="32h4siskw0wix">
                      {isSelected && <Check className="w-3 h-3" data-id="element-9175" data-mp="37azl6lih0lu5" />}
                    </div>
                  </div>;
          })}
            </div>
          </div>}

        {/* Deadline */}
        <div className="flex items-center gap-3" data-id="element-9176" data-mp="3gcpbtp4j3gqv">
          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-9177" data-mp="2dxzv10z800ms" />
          <div className="flex-1" data-id="element-9178" data-mp="2dvn5o2r4unzp">
            <label className="text-xs font-semibold text-slate-500 mb-0.5 block" data-id="element-9179" data-mp="2hya10dn0ir1n">
              Deadline (optioneel)
            </label>
            <Input type="date" value={deadline} onChange={e => setDeadline(e.target.value)} className="h-9 text-sm" min={new Date().toISOString().split('T')[0]} data-id="element-9180" data-mp="2mwl7qfi0glfn" />
          </div>
        </div>

        {/* Summary card */}
        <Card className="p-3.5 border-orange-200 bg-orange-50/40" data-id="element-9181" data-mp="31l7q6q2xptb3">
          <div className="flex items-center gap-3" data-id="element-9182" data-mp="35pp8slxk60ka">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${meta?.bgColor} ${meta?.color}`} data-id="element-9183" data-mp="35mt5sj6usloz">
              {Icon && <Icon className="w-5 h-5" data-id="element-9184" data-mp="1bc65evgujnr2" />}
            </div>
            <div className="flex-1 min-w-0" data-id="element-9185" data-mp="1g96bh6dp4qi6">
              <div className="font-bold text-slate-900 text-sm" data-id="element-9186" data-mp="1g9y4qajtmc5a">
                {meta?.label}
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-9187" data-mp="18rzzwjcl3p8e">
                {selectedFamilyIds.length} onderdelen
                <span className="text-slate-300 mx-1.5" data-id="element-9188" data-mp="z04093uunr22">·</span>
                {selectionMode === 'whole' ? `${students.length} leerlingen` : selectionMode === 'specific' ? `${selectedStudentIds.length} leerling(en)` : 'Nog niet gekozen'}
                {deadline && <>
                    <span className="text-slate-300 mx-1.5" data-id="element-9189" data-mp="1mnvbe44nly1r">·</span>
                    tot{' '}
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                  day: 'numeric',
                  month: 'short'
                })}
                  </>}
              </div>
            </div>
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 ${!isNextDisabled() ? 'text-emerald-500' : 'text-slate-300'}`} data-id="element-9190" data-mp="29l87o9ehw3c" />
          </div>
        </Card>
      </div>;
  };
  return <SlideOver open={open} onClose={onClose} title="Toets Klaarzetten" description={<span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold bg-orange-100 text-orange-700" data-id="element-9192" data-mp="1sf7xqo7x99ks">
          Groep {selectedGroup}
        </span>} width="lg" data-id="element-9191" data-mp="26e7oc1ezvyjl">
      <div className="flex flex-col h-full" data-id="element-9193" data-mp="2l457g2y2hy1x">
        {showSuccess ? <div className="flex-1 flex flex-col items-center justify-center p-6 text-center" data-id="element-9194" data-mp="2p4ytsrvv34sp">
            <motion.div initial={{
          scale: 0.8,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          bounce: 0.5
        }} className="mb-6" data-id="element-9195" data-mp="2bdkbkn9s37mp">
              <DjayNarrator size="lg" emotion="proud" data-id="element-9196" data-mp="38u9ao97cfvse" />
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.1
        }} className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mb-4" data-id="element-9197" data-mp="3i5f5j75fw9ld">
              <CheckCircle2 className="w-8 h-8" data-id="element-9198" data-mp="2za760yarinnu" />
            </motion.div>
            <motion.h2 initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.2
        }} className="text-2xl font-black text-slate-900 mb-2" data-id="element-9199" data-mp="2v8d9n27lo5th">
              Toets klaargezet! 🎉
            </motion.h2>
            <motion.p initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.3
        }} className="text-slate-600 mb-6 max-w-sm" data-id="element-9200" data-mp="jjc81hogugcm">
              De toets{' '}
              <span className="font-bold" data-id="element-9201" data-mp="1ujq59a19jq9x">
                {selectedDomain ? DOMAIN_META[selectedDomain].label : ''}
              </span>{' '}
              voor{' '}
              <span className="font-bold" data-id="element-9202" data-mp="2n6kk9rfb9xg0">
                {selectionMode === 'whole' ? 'de hele klas' : `${selectedStudentIds.length} leerling(en)`}
              </span>{' '}
              is klaargezet.
              {deadline && <>
                  {' '}
                  Deadline:{' '}
                  <span className="font-bold" data-id="element-9203" data-mp="3ayhytqa5b3kt">
                    {new Date(deadline).toLocaleDateString('nl-NL', {
                day: 'numeric',
                month: 'long'
              })}
                  </span>
                  .
                </>}
            </motion.p>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.4
        }} className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-8 max-w-sm w-full" data-id="element-9204" data-mp="3nf0sylv3iprd">
              <p className="text-sm text-slate-500" data-id="element-9205" data-mp="571mf2u2p6xr">
                Je kunt deze toets aanpassen of verwijderen op de
                Toetsen-pagina.
              </p>
            </motion.div>
            <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          delay: 0.5
        }} data-id="element-9206" data-mp="f10knqr106kf">
              <Button onClick={() => {
            onSuccess();
            onClose();
          }} className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 h-11 font-semibold" data-id="element-9207" data-mp="2k0xpjqon7ppc">
                Sluiten
              </Button>
            </motion.div>
          </div> : <>
            {/* Step indicator */}
            {renderStepIndicator()}

            {/* Step content with animation */}
            <div className="flex-1 overflow-hidden relative" data-id="element-9208" data-mp="2yrn6zasp611n">
              <AnimatePresence mode="wait" custom={direction} data-id="element-9209" data-mp="3cry0klle5tqi">
                <motion.div key={step} custom={direction} variants={stepVariants} initial="enter" animate="center" exit="exit" transition={{
              duration: 0.2,
              ease: 'easeInOut'
            }} className="absolute inset-0 overflow-y-auto px-0.5" data-id="element-9210" data-mp="3cpbpibzakw3j">
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Running summary + actions */}
            <div className="flex-shrink-0 bg-white" data-id="element-9211" data-mp="36bpi1qlb1z1z">
              {renderSummaryStrip()}

              <div className="flex items-center justify-between pt-3 pb-safe" data-id="element-9212" data-mp="3ba92tno5x4sr">
                <Button variant="ghost" onClick={step === 1 ? onClose : handleBack} className="text-slate-500 hover:text-slate-700 hover:bg-slate-100 h-11 px-4" data-id="element-9213" data-mp="3b8632bx7jxau">
                  {step === 1 ? 'Annuleren' : 'Terug'}
                </Button>

                {step < 3 ? <Button onClick={handleNext} disabled={isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9214" data-mp="zl9epzxv4j6x">
                    Volgende
                    <ChevronRight className="w-4 h-4 ml-1" data-id="element-9215" data-mp="vw4svgbwm4c" />
                  </Button> : <Button onClick={handleConfirm} disabled={isSubmitting || isNextDisabled()} className="bg-orange-500 hover:bg-orange-600 text-white shadow-sm h-11 px-6 font-semibold" data-id="element-9216" data-mp="3s2yvzmw62sek">
                    {isSubmitting ? 'Bezig...' : 'Klaarzetten'}
                  </Button>}
              </div>
            </div>
          </>}
      </div>
    </SlideOver>;
}